import { Breadcrumb, BreadcrumbItem } from '@carbon/react';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';

import style from './page-header.module.scss';

interface Props {
  breadcrumbs: [string, ReactNode][];
  title: ReactNode;
  actions: ReactNode;
  compactContent?: boolean;
  /**
   * The single-row header mock v13 gives the Well page: a 20px title beside a 24px
   * icon on one ~48px line, rather than this header's usual 28px title. Opt-in and off
   * everywhere else, so the 100-odd other pages keep the header they have.
   */
  compact?: boolean;
  aside?: ReactNode;
  subtitle?: string;
  titleDetail?: ReactNode;
  iconComponent?: ReactNode;
  stickyHeader?: boolean;
  attributes?: ReactNode;
  referrer?: JSX.Element | null;
  children?: ReactNode;
}

/**
 * A page's header: breadcrumbs, title, actions and optional tabs or details
 * beneath. A sticky header stays in view as the page scrolls.
 * @param {Props} props The header's content and options.
 * @return {JSX.Element} The header.
 */
export function PageHeader({
  breadcrumbs,
  children,
  title,
  actions,
  compactContent,
  compact,
  subtitle,
  titleDetail,
  aside,
  iconComponent,
  stickyHeader,
  attributes,
  referrer,
}: Props) {
  const [scroll, setScroll] = useState(false);
  const shouldUpdateScrollState = useRef(true);
  useEffect(() => {
    if (stickyHeader) {
      /** Sticks the header once the page leaves the top, releases it back there. */
      const handler = () => {
        if (shouldUpdateScrollState.current && window.scrollY !== 0) {
          setScroll(true);
          document.body.classList.add('sticky-header');
          shouldUpdateScrollState.current = false;
        }

        if (document.body && window.scrollY === 0) {
          setScroll(false);
          document.body.classList.remove('sticky-header');
          shouldUpdateScrollState.current = true;
        }
      };
      window.addEventListener('scroll', handler);
      return () => {
        window.removeEventListener('scroll', handler);
        document.body.classList.remove('sticky-header');
      };
    }
    return undefined;
  }, [stickyHeader]);

  // A sticky header publishes how much of it is in view (it compacts once the
  // page scrolls, and sticks a little above the top edge) as
  // --page-header-height, so content that sticks too can sit right below it.
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const header = headerRef.current;
    if (!stickyHeader || !header) return undefined;
    /** Writes the header's height in view to --page-header-height. */
    const publish = () => {
      const stickTop = Number.parseFloat(getComputedStyle(header).top) || 0;
      document.body.style.setProperty(
        '--page-header-height',
        `${header.offsetHeight + Math.min(stickTop, 0)}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.body.style.removeProperty('--page-header-height');
    };
  }, [stickyHeader, scroll]);

  return (
    <header
      ref={headerRef}
      className={classNames(style.header, scroll ? style.scrolled : null)}
    >
      {breadcrumbs.length !== 0 && (
        <div className={style.breadcrumbs}>
          <Breadcrumb noTrailingSlash>
            {breadcrumbs.map(([location, text], index) => {
              const isCurrent = index === breadcrumbs.length - 1;
              return (
                <Crumb key={location} {...{ location, text, isCurrent }} />
              );
            })}
          </Breadcrumb>
          {referrer}
        </div>
      )}
      <div
        className={classNames(
          style.content,
          attributes ? style.attributesHeader : null,
          compactContent ? style.compactContent : null,
          compact ? style.compact : null
        )}
      >
        <div className={style.contentMain}>
          {subtitle ? <h2>{subtitle}</h2> : null}
          <div className={style.title}>
            <div className={style.titleColumn}>
              {typeof title === 'string' ? (
                <h1>
                  {iconComponent && (
                    <span className={style.icon}>{iconComponent}</span>
                  )}
                  {title}
                </h1>
              ) : (
                title
              )}

              {titleDetail}
            </div>
            {attributes && <div>{attributes}</div>}
            {aside && <aside>{aside}</aside>}
          </div>
        </div>
        {actions && <div className={style.actions}>{actions}</div>}
      </div>
      {children && <div className={style.children}>{children}</div>}
    </header>
  );
}

interface CrumbProps {
  location: string;
  text: ReactNode;
  isCurrent: boolean;
}

/**
 * One breadcrumb, linking to its page.
 * @param {CrumbProps} props The crumb.
 * @return {JSX.Element} The crumb.
 */
export function Crumb({ location, text, isCurrent }: CrumbProps) {
  return (
    <BreadcrumbItem isCurrentPage={isCurrent}>
      <NavLink exact to={location}>
        {text}
      </NavLink>
    </BreadcrumbItem>
  );
}
